import React, { createContext, useContext, useEffect, useState } from 'react';
import api, { IS_FRONT_ONLY } from 'lib/api';
import { getAccessToken, getRefreshToken, getSessionIdentity, storeSessionTokens, clearSessionTokens, removeLegacyTokens,
  getSessionRemaining, recordUserActivity, expireSession, SESSION_EXPIRED_EVENT, WARNING_MS } from 'lib/session';

const getStoredLanguage = () => (localStorage.getItem('blablacolis_language') === 'en' ? 'en' : 'fr');

const AuthContext = createContext(null);
const DEMO_USER = {
  id: 'demo-user',
  full_name: 'Demo User',
  email: 'demo@colibuddy.local',
  roles: ['sender', 'traveler', 'deliverer'],
  is_admin: false,
  wallet_balance: 0,
  deliverer_validated: false,
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(IS_FRONT_ONLY ? DEMO_USER : null);
  const [loading, setLoading] = useState(!IS_FRONT_ONLY);
  const [expiryWarning, setExpiryWarning] = useState(false);

  useEffect(() => {
    removeLegacyTokens();
    if (IS_FRONT_ONLY) {
      storeSessionTokens('demo-token', null, true);
      setUser(DEMO_USER);
      setLoading(false);
      return;
    }

    const token = getAccessToken();
    const identity = getSessionIdentity();
    if (token) {
      api.get('/auth/me')
        .then((r) => { if (getAccessToken() && identity === getSessionIdentity()) setUser(r.data); })
        .catch(() => {
          if (identity === getSessionIdentity()) clearSessionTokens();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const expired = () => { setUser(null); setExpiryWarning(false); };
    window.addEventListener(SESSION_EXPIRED_EVENT, expired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, expired);
  }, []);

  useEffect(() => {
    if (!user || IS_FRONT_ONLY) return;
    const check = () => {
      const remaining = getSessionRemaining();
      if (remaining <= 0) {
        const token = getRefreshToken();
        if (token) api.post('/auth/logout', { refresh_token: token }).catch(() => {});
        expireSession();
      } else setExpiryWarning(remaining <= WARNING_MS);
    };
    const activity = () => {
      try { recordUserActivity(); setExpiryWarning(getSessionRemaining() <= WARNING_MS); }
      catch { /* expiry event clears the authenticated UI */ }
    };
    const events = ['pointerdown', 'keydown', 'touchstart', 'wheel'];
    events.forEach(event => window.addEventListener(event, activity, { passive: true }));
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    const timer = setInterval(check, 1000);
    check();
    return () => {
      clearInterval(timer);
      events.forEach(event => window.removeEventListener(event, activity));
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [user?.id]);

  const login = async (email, password) => {
    if (IS_FRONT_ONLY) {
      storeSessionTokens('demo-token', null, true);
      setUser(DEMO_USER);
      return DEMO_USER;
    }

    const r = await api.post('/auth/login', { email, password });
    if (r.data?.requires_2fa) {
      return { requiresTwoFactor: true, userId: r.data.user_id };
    }
    storeSessionTokens(r.data.access_token, r.data.refresh_token, true);
    setUser(r.data.user);
    return r.data.user;
  };

  const verifyTwoFactor = async (userId, code) => {
    const r = await api.post('/auth/2fa/verify', { user_id: userId, code });
    storeSessionTokens(r.data.access_token, r.data.refresh_token, true);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (data) => {
    if (IS_FRONT_ONLY) {
      storeSessionTokens('demo-token', null, true);
      setUser({ ...DEMO_USER, full_name: data?.full_name || DEMO_USER.full_name, email: data?.email || DEMO_USER.email });
      return { ...DEMO_USER, full_name: data?.full_name || DEMO_USER.full_name, email: data?.email || DEMO_USER.email };
    }

    const r = await api.post('/auth/register', { ...data, language: getStoredLanguage() });
    storeSessionTokens(r.data.access_token, r.data.refresh_token, true);
    setUser(r.data.user);
    return r.data.user;
  };

  const socialLogin = async (provider, payload) => {
    if (IS_FRONT_ONLY) {
      storeSessionTokens('demo-token', null, true);
      setUser(DEMO_USER);
      return DEMO_USER;
    }

    const r = await api.post('/auth/social', { provider, ...payload, language: getStoredLanguage() });
    if (r.data?.requires_2fa) return { requiresTwoFactor: true, userId: r.data.user_id };
    storeSessionTokens(r.data.access_token, r.data.refresh_token, true);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    if (IS_FRONT_ONLY) {
      storeSessionTokens('demo-token', null, true);
      setUser(DEMO_USER);
      return;
    }

    const refreshToken = getRefreshToken();
    if (refreshToken) {
      api.post('/auth/logout', { refresh_token: refreshToken }).catch(() => {});
    }
    clearSessionTokens();
    setUser(null);
    setExpiryWarning(false);
  };

  const refreshUser = async () => {
    if (IS_FRONT_ONLY) return;

    try {
      const identity = getSessionIdentity();
      const r = await api.get('/auth/me');
      if (getAccessToken() && identity === getSessionIdentity()) setUser(r.data);
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyTwoFactor, register, socialLogin, logout, refreshUser, setUser }}>
      {children}
      {user && expiryWarning && (
        <div role="alert" className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-lg rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950 shadow-lg">
          <p>{getStoredLanguage() === 'en' ? 'Your session expires in less than 2 minutes. Activity can extend the inactivity limit, but not the 12-hour limit.' : 'Votre session expire dans moins de 2 minutes. Une activité prolonge le délai d’inactivité, mais pas la limite de 12 heures.'}</p>
          <button type="button" className="mt-2 font-semibold underline" onClick={() => { try { recordUserActivity(); setExpiryWarning(getSessionRemaining() <= WARNING_MS); } catch {} }}>
            {getStoredLanguage() === 'en' ? 'Stay active' : 'Rester actif'}
          </button>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
